import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  VpnKey as VpnKeyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TrendingUp as StatsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { externalApiService } from '../services/externalApiService';
import { ROUTES, EXTERNAL_API, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../constants';
import WebhookManager from '../components/WebhookManager';
import ApiKeyDashboard from '../components/ApiKeyDashboard';

const ExternalApi = () => {
  const navigate = useNavigate();

  // State
  const [currentTab, setCurrentTab] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, apiKey: null });
  const [regenerateDialog, setRegenerateDialog] = useState({ open: false, apiKey: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [dashboardDialog, setDashboardDialog] = useState({ open: false, apiKey: null });

  // Load API Keys
  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await externalApiService.getApiKeys({
        include_inactive: filterStatus === 'all' || filterStatus === 'inactive',
        page: 1,
        limit: 20
      });
      
      // Resposta agora tem estrutura: { items, pagination, stats }
      setApiKeys(response.items || []);
    } catch (err) {
      console.error('Erro ao carregar API Keys:', err);
      setError('Erro ao carregar API Keys. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, [filterStatus]);

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Filter API Keys
  const filteredApiKeys = apiKeys.filter(key => {
    const matchesSearch = key.key_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         key.key_prefix?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && key.is_active) ||
                         (filterStatus === 'inactive' && !key.is_active) ||
                         (filterStatus === 'expired' && key.expires_at && new Date(key.expires_at) < new Date());
                         
    return matchesSearch && matchesFilter;
  });

  // Handle Delete
  const handleDelete = async () => {
    try {
      await externalApiService.deleteApiKey(deleteDialog.apiKey.id);
      setSuccessMessage('API Key deletada com sucesso!');
      setDeleteDialog({ open: false, apiKey: null });
      loadApiKeys();
    } catch (err) {
      console.error('Erro ao deletar API Key:', err);
      setError('Erro ao deletar API Key');
    }
  };

  // Handle Regenerate
  const handleRegenerate = async () => {
    try {
      const newKey = await externalApiService.regenerateApiKey(regenerateDialog.apiKey.id);
      setSuccessMessage('API Key regenerada com sucesso!');
      setRegenerateDialog({ open: false, apiKey: null });
      
      // Show the new key temporarily
      const updatedKeys = apiKeys.map(key => 
        key.id === newKey.id ? { ...newKey, showFullKey: true } : key
      );
      setApiKeys(updatedKeys);
      
      // Hide the key after 30 seconds
      setTimeout(() => {
        setApiKeys(prev => prev.map(key => 
          key.id === newKey.id ? { ...key, showFullKey: false } : key
        ));
      }, 30000);
      
    } catch (err) {
      console.error('Erro ao regenerar API Key:', err);
      setError('Erro ao regenerar API Key');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage(`${label} copiado!`);
  };

  // Get status info
  const getStatusInfo = (apiKey) => {
    if (!apiKey.is_active) {
      return { color: 'error', label: 'Inativa', icon: <ErrorIcon /> };
    }
    
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) {
      return { color: 'warning', label: 'Expirada', icon: <WarningIcon /> };
    }
    
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) {
      return { color: 'warning', label: 'Expira em breve', icon: <ScheduleIcon /> };
    }
    
    return { color: 'success', label: 'Ativa', icon: <CheckCircleIcon /> };
  };

  // Get tier info
  const getTierInfo = (tier) => {
    return externalApiService.getTierDisplayInfo(tier);
  };

  // Handle menu
  const handleMenuOpen = (event, apiKey) => {
    setMenuAnchor(event.currentTarget);
    setSelectedKey(apiKey);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedKey(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Controls */}
      <Box sx={{ mb: 4 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="standard"
          sx={{ mb: 3 }}
        >
          <Tab label="API Keys" icon={<VpnKeyIcon />} />
          <Tab label="Webhooks" icon={<WebhookIcon />} />
        </Tabs>
        
        {/* Controls for each tab */}
        {currentTab === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Gerencie as API Keys e Webhooks para integração com aplicações externas
            </Typography>
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => navigate(ROUTES.EXTERNAL_API_CREATE)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Nova API Key
            </Button>
          </Box>
        )}
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Box>
          {/* Search and Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
            {/* Search */}
            <TextField
              placeholder="Buscar API Keys..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Status Filter Chips */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { value: 'all', label: 'Todas' },
                { value: 'active', label: 'Ativas' },
                { value: 'inactive', label: 'Inativas' },
                { value: 'expired', label: 'Expiradas' },
              ].map((filter) => (
                <Chip
                  key={filter.value}
                  label={filter.label}
                  color={filterStatus === filter.value ? 'primary' : 'default'}
                  variant={filterStatus === filter.value ? 'filled' : 'outlined'}
                  onClick={() => setFilterStatus(filter.value)}
                  size="small"
                />
              ))}
            </Box>
          </Box>

        {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* API Keys Grid */}
      {filteredApiKeys.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <VpnKeyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {searchTerm || filterStatus !== 'all' 
                ? 'Nenhuma API Key encontrada' 
                : 'Nenhuma API Key criada ainda'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || filterStatus !== 'all'
                ? 'Tente ajustar os filtros de busca.'
                : 'Crie sua primeira API Key para começar a integrar aplicações externas.'
              }
            </Typography>
            {!searchTerm && filterStatus === 'all' && (
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => navigate(ROUTES.EXTERNAL_API_CREATE)}
              >
                Criar primeira API Key
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredApiKeys.map((apiKey) => {
            const statusInfo = getStatusInfo(apiKey);
            const tierInfo = getTierInfo(apiKey.rate_limit_tier);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={apiKey.id}>
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: (theme) => theme.shadows[4],
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" component="h3" sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {apiKey.key_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {apiKey.showFullKey ? apiKey.api_key : apiKey.key_preview}
                        </Typography>
                      </Box>
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, apiKey)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    {/* Status & Tier */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        color={statusInfo.color}
                        size="small"
                      />
                      <Chip
                        label={`${tierInfo.icon} ${tierInfo.label}`}
                        color={tierInfo.color}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    {/* Permissions */}
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong>Agentes:</strong> {apiKey.allowed_agents?.length 
                        ? apiKey.allowed_agents.join(', ')
                        : 'Todos'
                      }
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong>Canais:</strong> {apiKey.allowed_channels?.join(', ') || 'API'}
                    </Typography>

                    {/* Usage Stats */}
                    {apiKey.usage_count > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        <strong>Uso:</strong> {apiKey.usage_count.toLocaleString()} requests
                      </Typography>
                    )}

                    {/* Expiration */}
                    {apiKey.expires_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        <strong>Expira:</strong> {new Date(apiKey.expires_at).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Box>
                      <Tooltip title="Ver estatísticas">
                        <IconButton
                          size="small"
                          onClick={() => setDashboardDialog({ open: true, apiKey })}
                        >
                          <StatsIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(ROUTES.EXTERNAL_API_EDIT.replace(':keyId', apiKey.id))}
                    >
                      Editar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16, display: { xs: 'flex', sm: 'none' } }}
        onClick={() => navigate(ROUTES.EXTERNAL_API_CREATE)}
      >
        <AddIcon />
      </Fab>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          navigate(ROUTES.EXTERNAL_API_EDIT.replace(':keyId', selectedKey.id));
          handleMenuClose();
        }}>
          <ListItemIcon><EditIcon /></ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          setDashboardDialog({ open: true, apiKey: selectedKey });
          handleMenuClose();
        }}>
          <ListItemIcon><StatsIcon /></ListItemIcon>
          <ListItemText>Estatísticas</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => {
          setRegenerateDialog({ open: true, apiKey: selectedKey });
          handleMenuClose();
        }}>
          <ListItemIcon><RefreshIcon /></ListItemIcon>
          <ListItemText>Regenerar Key</ListItemText>
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            setDeleteDialog({ open: true, apiKey: selectedKey });
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
          <ListItemText>Deletar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, apiKey: null })}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar a API Key <strong>"{deleteDialog.apiKey?.key_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            ⚠️ Esta ação é irreversível e todas as aplicações que usam esta API Key deixarão de funcionar imediatamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, apiKey: null })}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Deletar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Dialog */}
      <Dialog open={regenerateDialog.open} onClose={() => setRegenerateDialog({ open: false, apiKey: null })}>
        <DialogTitle>Regenerar API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja regenerar a API Key <strong>"{regenerateDialog.apiKey?.key_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            ⚠️ A API Key atual será invalidada imediatamente. Você precisará atualizar todas as integrações com a nova key.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateDialog({ open: false, apiKey: null })}>
            Cancelar
          </Button>
          <Button onClick={handleRegenerate} color="primary" variant="contained">
            Regenerar
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}

      {/* Webhooks Tab */}
      {currentTab === 1 && (
        <WebhookManager />
      )}

      {/* Dashboard de Estatísticas */}
      <ApiKeyDashboard
        open={dashboardDialog.open}
        onClose={() => setDashboardDialog({ open: false, apiKey: null })}
        apiKey={dashboardDialog.apiKey}
      />
    </Box>
  );
};

export default ExternalApi;
